import { Pagination, RecordContent, RecordViewMode } from '@aipb/gulliver/dynamic-gallery/model';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { Fancybox } from '@fancyapps/ui';
import { Lightbox } from 'ngx-lightbox';

interface FancyboxImage {
  thumb: string;
  original: string;
  caption: string;
}

@Component({
  selector: 'dg-record-list',
  templateUrl: './record-list.component.html',
  styleUrls: ['./record-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecordListComponent implements OnInit {
  @Input() collectionLabel: string;
  @Input() viewMode: RecordViewMode;
  @Input() activeRecordId: string;

  @Input() pagination?: Pagination;

  defaultImage = 'https://via.placeholder.com/100x150';

  constructor(private _lightbox: Lightbox) {}

  private _fancyboxImages: FancyboxImage[] = [];

  get fancyboxImages() {
    return this._fancyboxImages;
  }

  private _items: RecordContent[] = [];

  get items() {
    return this._items;
  }

  @Input() set items(value: RecordContent[]) {
    this._items = value;

    this._fancyboxImages = value.map((record) => {
      const original = record.images[0].main;
      const thumb = record.images3if ? record.images3if[0] + 'full/,150/0/default.jpg' : record.images[0].thumb;

      return {
        original,
        thumb,
        caption: record.html
      };
    });
  }

  ngOnInit(): void {
    Fancybox.bind('[data-fancybox="gallery"]', {
      Toolbar: {
        display: [
          {
            id: 'counter',
            position: 'center'
          },
          'zoom',
          'slideshow',
          'fullscreen',
          'thumbs',
          'close'
        ]
      },

      on: {
        initLayout: (fancybox) => {
          // Create elements
          // ===

          // Create left column
          const $leftCol = document.createElement('div');
          $leftCol.classList.add('fancybox__leftCol', 'w-full', 'md:w-6/12', 'lg:w-9/12');

          while (fancybox.$container.firstChild) {
            $leftCol.appendChild(fancybox.$container.firstChild);
          }

          // Create right column
          const $rightCol = document.createElement('div');
          $rightCol.classList.add('fancybox__rightCol', 'hidden', 'md:block', 'md:w-6/12', 'lg:w-3/12');

          $rightCol.innerHTML = '<h1 class="mb-2 text-lg font-bold">' + this.collectionLabel + '</h1>';

          // Create info-box
          const $info = document.createElement('div');
          $rightCol.appendChild($info);
          fancybox.$info = $info;

          // Add elements to DOM
          fancybox.$container.appendChild(fancybox.$backdrop);

          fancybox.$container.appendChild($leftCol);
          fancybox.$container.appendChild($rightCol);

          fancybox.$leftCol = $leftCol;
          fancybox.$rightCol = $rightCol;
        },
        'Carousel.ready Carousel.change': (fancybox, carousel, slideIndex) => {
          // Update info-box
          // ===

          // Get index of the current gallery item
          slideIndex = slideIndex === undefined ? carousel.options.initialPage : slideIndex;

          // Get link related to current item
          const $trigger = fancybox.items[slideIndex].$trigger;

          // Get data from `data-info` attribute
          const data = $trigger.dataset.info || '';

          // Update info
          fancybox.$info.innerHTML = `<p>${data}</p>`;
        }
      }
    });
  }

  isMasonry() {
    return this.viewMode === 'masonry';
  }

  isMasonryWithLabel() {
    return this.viewMode === 'masonry-with-label';
  }

  isFancybox() {
    return this.viewMode === 'fancybox';
  }

  isOpenLayers() {
    return this.viewMode === 'open-layers';
  }
}
